/**
 * ResetMfaFlow
 * ============
 *
 * Shared confirmation flow for resetting (removing all) MFA factors from a
 * person's account.  Mounted by three parent surfaces:
 *
 *   - ProviderEditPatientForm   (patient overview - Edit Patient Details)
 *   - InformantManagementContent (informant detail screen)
 *   - ProviderEditUserForm      (staff dashboard - Edit Provider Details)
 *
 * Each parent is responsible for its own action card and gating logic.  This
 * component only renders once the caller has clicked "Reset two-factor
 * authentication" and owns the confirm -> loading -> success state machine.
 *
 * Modelled on ResendWelcomeFlow and RemoveInformantFlow in the informant
 * management system — same props contract, same two-phase render pattern
 * (confirm screen vs ConfirmationScreen on success), same mountedRef guard.
 *
 * The confirm button uses destructive (red) styling to match the Disable
 * Account confirmation pattern in ProviderEditUserForm.
 *
 * Outcome branching:
 *   The Cloud Function returns { success, alreadyReset, removedCount,
 *   removedFactorTypes }.  When `alreadyReset` is true (no factors were
 *   found in Auth), the success screen shows a distinct "no methods found"
 *   message instead of the standard "all methods removed" copy.  This
 *   handles cases where Firestore was stale (isMFASetUp: true) but Auth
 *   had already been cleared.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import WarningPanel from '../informantManagement/WarningPanel';
import ConfirmationScreen from '../informantManagement/ConfirmationScreen';
import ButtonSpinner from '../informantManagement/ButtonSpinner';

/**
 * Human-readable label for the MFA method stored in Firestore.
 * Used in both the confirmation summary card and the action card
 * descriptions across the three parent surfaces.
 *
 * Falls back gracefully for legacy docs that have isMFASetUp: true but
 * no mfaMethod field, and for docs where isMFASetUp is false/missing.
 */
function getMfaMethodLabel(mfaMethod, isMFASetUp) {
    if (mfaMethod === 'totp') return 'Authenticator app';
    if (mfaMethod === 'phone') return 'SMS';
    if (isMFASetUp === true) return 'Set up (type not recorded)';
    return 'No method recorded';
}

/**
 * Whether the target is a service user (patient or informant) rather than
 * a provider user (clinician / support / Org. Admin).  Service users may
 * choose to skip MFA on re-enrolment; provider users must always set it up.
 */
function isServiceUser(targetUserType) {
    return targetUserType === 'patient' || targetUserType === 'informant';
}

/**
 * @param {Object}        props
 * @param {Object}        props.targetUser       – { firstName, lastName, email, isMFASetUp, mfaMethod }
 * @param {string}        props.targetUserType   – 'patient' | 'informant' | 'clinician' — drives copy
 * @param {Function}      props.onReset          – () => Promise<{success, alreadyReset, removedCount, removedFactorTypes}>
 * @param {Function}      props.onBack           – Navigate back to summary / detail
 * @param {Function}      props.onFinish         – Called after the success screen "Finish" button
 * @param {boolean}       props.actionLoading    – Whether the backend call is in progress
 * @param {string|null}   props.actionError      – Error message from the backend
 * @param {Function}      props.clearActionState – Reset error/success state in parent
 */
export default function ResetMfaFlow({
    targetUser,
    targetUserType = 'patient',
    onReset,
    onBack,
    onFinish,
    actionLoading = false,
    actionError = null,
    clearActionState,
}) {
    // Internal success state — parent does not track this.  The flow swaps
    // its entire view to ConfirmationScreen once the Cloud Function resolves.
    const [isSuccess, setIsSuccess] = useState(false);

    // Stores the backend response payload so the success screen can branch
    // between "factors removed" and "no factors found" (alreadyReset).
    const [resultPayload, setResultPayload] = useState(null);

    // Guard against setState after unmount (e.g. modal closed mid-request).
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Call the parent's reset handler, capture the returned payload, and
    // transition to the success screen on resolve.  Errors are surfaced via
    // actionError (set by the parent's hook / callFunction wrapper).
    const handleConfirm = useCallback(async () => {
        try {
            const payload = await onReset();
            if (mountedRef.current) {
                setResultPayload(payload || null);
                setIsSuccess(true);
            }
        } catch {
            // actionError is set by the parent hook; nothing to do here.
        }
    }, [onReset]);

    // Navigate back, clearing any stale error state first.
    const handleBack = useCallback(() => {
        clearActionState?.();
        onBack();
    }, [clearActionState, onBack]);

    // Build role-aware copy for the warning and success messages.
    const targetName = `${targetUser.firstName} ${targetUser.lastName}`;
    const isServiceTarget = isServiceUser(targetUserType);

    // Service users (patients, informants) can choose to skip MFA on next
    // sign-in; provider users (clinicians, support, Org. Admins) are always
    // required to set it up before proceeding.
    const nextSignInCopy = isServiceTarget
        ? 'When they next sign in, they will be prompted to set up two-factor authentication again, or they may choose to skip it.'
        : 'When they next sign in, they will be required to set up two-factor authentication again before they can access the platform.';

    // -- Success screen -------------------------------------------------------
    if (isSuccess) {
        // Branch between "factors removed" and "no factors found" based on
        // the backend response.  The alreadyReset path handles cases where
        // Auth had no factors (possibly cleared by the admin script or a
        // concurrent reset), but Firestore still showed isMFASetUp: true.
        const alreadyReset = resultPayload?.alreadyReset === true;

        if (alreadyReset) {
            return (
                <ConfirmationScreen
                    headline="No two-factor authentication methods found"
                    detail={
                        `${targetName}'s account had no two-factor authentication methods ` +
                        `to remove. The account record has been updated to match. ` +
                        nextSignInCopy
                    }
                    onFinish={onFinish}
                />
            );
        }

        return (
            <ConfirmationScreen
                headline="Two-factor authentication reset"
                detail={
                    `All two-factor authentication methods have been removed from ` +
                    `${targetName}'s account. ${nextSignInCopy}`
                }
                onFinish={onFinish}
            />
        );
    }

    // -- Confirmation screen --------------------------------------------------
    const methodLabel = getMfaMethodLabel(targetUser.mfaMethod, targetUser.isMFASetUp);

    return (
        <div className="space-y-3">
            {/* Back link — same pattern as ResendWelcomeFlow / edit flows */}
            <button
                type="button"
                onClick={handleBack}
                disabled={actionLoading}
                className="text-xs text-violet-600 hover:text-violet-800 font-semibold disabled:opacity-50"
            >
                &larr; Back
            </button>

            <h3 className="text-base font-semibold text-gray-900">
                Reset two-factor authentication
            </h3>

            {/* Summary card — matches the confirm screens in
                ProviderEditPatientForm and ProviderEditUserForm */}
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <dl
                    className="grid gap-x-3 gap-y-1 text-xs"
                    style={{ gridTemplateColumns: 'auto 1fr' }}
                >
                    <dt className="text-gray-600">Name</dt>
                    <dd className="font-medium text-gray-800">
                        {targetName}
                    </dd>
                    {targetUser.email && (
                        <>
                            <dt className="text-gray-600">Email</dt>
                            <dd className="font-medium text-gray-800 break-all">
                                {targetUser.email}
                            </dd>
                        </>
                    )}
                    <dt className="text-gray-600">Recorded method</dt>
                    <dd className="font-medium text-gray-800">
                        {methodLabel}
                    </dd>
                </dl>
                {/* Hint: Firestore may be stale; Auth is checked on the server */}
                <p className="text-xs text-gray-400 mt-1.5 italic">
                    The recorded method is what the platform last saved; the reset checks the sign-in system directly.
                </p>
            </div>

            {/* Warning panel — explains what will happen and what will not */}
            <WarningPanel variant="warning">
                <ul className="list-disc pl-4 space-y-1">
                    <li>All two-factor authentication methods will be removed from {targetName}'s account.</li>
                    <li>{nextSignInCopy}</li>
                    <li>If {targetName} is currently signed in, their session will not be interrupted. The change takes effect on their next sign-in.</li>
                </ul>
            </WarningPanel>

            {/* Error display — shown when the Cloud Function call fails */}
            {actionError && (
                <WarningPanel variant="error">{actionError}</WarningPanel>
            )}

            {/* Action buttons — Cancel (grey) + destructive confirm (red) */}
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={handleBack}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={actionLoading}
                    className={`flex-1 px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors ${
                        actionLoading
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-red-600 hover:bg-red-700'
                    }`}
                >
                    {actionLoading ? (
                        <span className="inline-flex items-center justify-center gap-2">
                            <ButtonSpinner />
                            Resetting...
                        </span>
                    ) : (
                        'Reset two-factor authentication'
                    )}
                </button>
            </div>
        </div>
    );
}
